/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react'
import { ConfigurationContext } from '@news-mono/web-common'
import { v4 as uuidv4 } from 'uuid'
import { useLocation } from 'react-router-dom'
import { LoadingEllipsis } from '../../LoadingEllipsis'
import {
    PollContainer,
    PollLogo,
    PollQuestion,
    OptionsContainer,
    OptionLabel,
    OptionInput,
    OptionText,
    SubmitButton,
    ResultsContainer,
    ResultItem,
    ResultText,
    ResultPercentage,
    ResultBar,
    ResultBarFill,
    ResultBarContent,
    TotalVotes,
    ResponseStatus,
    VoteErrorMessage,
    ShareLinkContainer,
    LoadingContainer,
} from './Poll.styled'

const sevenNewsLogo = require('../../content/SevenNewsHeaderV2/logos/7NEWS_Logo_RED_RGB.svg')
const sevenYouLogo = require('../../content/SevenNewsHeaderV2/logos/7You_Logo.svg')

export interface PollOption {
    optionId: string
    optionText: string
    voteCount: number
}
interface PollProps {
    pollId: string
}

interface PollApiResponse {
    pollId: string
    title: string
    description: string | null
    closeTime: string | null
    options: PollOption[]
}

interface PollStorageValue {
    sessionId: string
    hasVoted: boolean
    selectedOption: string | null
}

interface OptimisticVoteState {
    optionId: string
    minimumVoteCount: number
}

const getPollStorageKey = (pollId: string): string => `poll:${pollId}`

const readPollStorageValue = (key: string): PollStorageValue | null => {
    try {
        const rawValue = globalThis.localStorage.getItem(key)
        if (!rawValue) {
            return null
        }

        const parsedValue = JSON.parse(rawValue) as Partial<PollStorageValue>
        if (
            typeof parsedValue.sessionId !== 'string' ||
            typeof parsedValue.hasVoted !== 'boolean' ||
            (parsedValue.selectedOption !== undefined &&
                parsedValue.selectedOption !== null &&
                typeof parsedValue.selectedOption !== 'string')
        ) {
            return null
        }

        return {
            sessionId: parsedValue.sessionId,
            hasVoted: parsedValue.hasVoted,
            selectedOption: parsedValue.selectedOption ?? null,
        }
    } catch {
        return null
    }
}

const writePollStorageValue = (key: string, value: PollStorageValue): void => {
    try {
        globalThis.localStorage.setItem(key, JSON.stringify(value))
    } catch {
        // Ignore storage write failures (e.g. private mode quota issues).
    }
}

const getOrCreatePollStorageValue = (
    pollStorageKey: string,
): PollStorageValue => {
    const existingPollStorage = readPollStorageValue(pollStorageKey)
    if (existingPollStorage) {
        return existingPollStorage
    }

    const newPollStorageValue = {
        sessionId: uuidv4(),
        hasVoted: false,
        selectedOption: null,
    }
    writePollStorageValue(pollStorageKey, newPollStorageValue)
    return newPollStorageValue
}

const POLL_SUBMIT_ERROR_MESSAGE =
    'We could not save your vote right now. Please try again.'

const isPollClosedByCloseTime = (closeTime: string | null): boolean => {
    if (!closeTime) {
        return false
    }

    const closeTimeTimestamp = Date.parse(closeTime)
    if (Number.isNaN(closeTimeTimestamp)) {
        return false
    }

    return closeTimeTimestamp <= Date.now()
}

const sortOptionsByOptionId = (options: PollOption[]): PollOption[] => {
    return [...options].sort((optionA, optionB) =>
        optionA.optionId.localeCompare(optionB.optionId),
    )
}

const applyOptimisticVote = (
    options: PollOption[],
    optimisticVoteRef: React.MutableRefObject<OptimisticVoteState | null>,
): PollOption[] => {
    const optimisticVote = optimisticVoteRef.current
    if (!optimisticVote) {
        return options
    }

    const selectedOption = options.find(
        (option) => option.optionId === optimisticVote.optionId,
    )

    if (!selectedOption) {
        return options
    }

    if (selectedOption.voteCount >= optimisticVote.minimumVoteCount) {
        optimisticVoteRef.current = null
        return options
    }

    return options.map((option) =>
        option.optionId === optimisticVote.optionId
            ? {
                  ...option,
                  voteCount: optimisticVote.minimumVoteCount,
              }
            : option,
    )
}

const PollComponent: React.FC<PollProps> = ({ pollId }) => {
    const location = useLocation()
    const config = React.useContext(ConfigurationContext)
    const [hasVoted, setHasVoted] = useState<boolean>(false)
    const [selectedOption, setSelectedOption] = useState<string | null>(null)
    const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(
        null,
    )
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const [pollQuestion, setPollQuestion] = useState<string>('')
    const [pollCloseTime, setPollCloseTime] = useState<string | null>(null)
    const [pollOptions, setPollOptions] = useState<PollOption[]>([])
    const [isInitialPollLoading, setIsInitialPollLoading] =
        useState<boolean>(true)
    const reloadRef = React.useRef<NodeJS.Timeout | null>(null)
    const optimisticVoteRef = React.useRef<OptimisticVoteState | null>(null)
    const pollSessionIdRef = React.useRef<string>('')
    const isSubmittingRef = React.useRef<boolean>(false)
    const pollStorageKey = getPollStorageKey(pollId)

    useEffect(() => {
        optimisticVoteRef.current = null
        pollSessionIdRef.current = ''
        isSubmittingRef.current = false
        setHasVoted(false)
        setSelectedOption(null)
        setIsSubmitting(false)
        setSubmitErrorMessage(null)
        setPollQuestion('')
        setPollCloseTime(null)
        setPollOptions([])
        setIsInitialPollLoading(true)
    }, [pollId])

    useEffect(() => {
        const pollStorage = getOrCreatePollStorageValue(pollStorageKey)
        pollSessionIdRef.current = pollStorage.sessionId
        setHasVoted(pollStorage.hasVoted)
        setSelectedOption(pollStorage.selectedOption)
    }, [pollStorageKey])

    const fetchAndApplyPoll = useCallback(
        async (shouldApply?: () => boolean): Promise<void> => {
            if (!config?.contentApi) return

            const response = await fetch(
                `${config.contentApi}/v4/poll/${pollId}`,
                {
                    headers: {
                        caller: config.apiCallerHeader,
                    },
                    cache: 'no-store',
                },
            )

            if (!response.ok) {
                throw new Error(
                    `Failed to fetch poll ${pollId}: ${response.status}`,
                )
            }

            const pollData = (await response.json()) as PollApiResponse

            if (shouldApply && !shouldApply()) return

            setPollQuestion(pollData.description || pollData.title || '')
            setPollCloseTime(pollData.closeTime ?? null)
            const optionsWithOptimisticVote = applyOptimisticVote(
                pollData.options || [],
                optimisticVoteRef,
            )
            setPollOptions(sortOptionsByOptionId(optionsWithOptimisticVote))
        },
        [setPollOptions, setPollQuestion, setPollCloseTime, pollId, config],
    )

    useEffect(() => {
        let isEffectActive = true

        const loadPoll = async () => {
            if (!config?.contentApi) {
                if (isEffectActive) setIsInitialPollLoading(false)
                return
            }
            if (!isEffectActive) return

            try {
                await fetchAndApplyPoll(() => isEffectActive)
            } catch (error) {
                console.error(error)
            } finally {
                if (isEffectActive) {
                    setIsInitialPollLoading(false)
                }
            }
        }

        void loadPoll()

        if (config?.contentApi) {
            const configuredReloadIntervalMs =
                config.pollReloadIntervalMs ?? 20000
            const reloadIntervalMs =
                configuredReloadIntervalMs > 0
                    ? configuredReloadIntervalMs
                    : 20000

            reloadRef.current = setInterval(() => {
                if (isEffectActive) {
                    void loadPoll()
                }
            }, reloadIntervalMs)
        }
        return () => {
            isEffectActive = false
            if (reloadRef.current) {
                clearInterval(reloadRef.current)
                reloadRef.current = null
            }
        }
    }, [config, fetchAndApplyPoll, pollId])

    const totalVotes: number = pollOptions.reduce(
        (sum: number, option: PollOption) => sum + option.voteCount,
        0,
    )
    const isPollClosed = isPollClosedByCloseTime(pollCloseTime)
    const shouldShowResults = hasVoted || isPollClosed
    const isSevenYouPath = location.pathname.startsWith('/7you')
    const pollLogo = isSevenYouPath ? sevenYouLogo : sevenNewsLogo

    // Avoid rendering an empty poll card if the poll failed to load.
    if (!isInitialPollLoading && pollOptions.length === 0 && !pollQuestion) {
        return null
    }
    const handleOptionChange = (optionId: string): void => {
        setSelectedOption(optionId)
        setSubmitErrorMessage(null)
        const pollStorage = getOrCreatePollStorageValue(pollStorageKey)
        writePollStorageValue(pollStorageKey, {
            ...pollStorage,
            selectedOption: optionId,
        })
    }

    const handleSubmit = (): void => {
        if (selectedOption && !isSubmittingRef.current && !isPollClosed) {
            void submitVote(selectedOption)
        }
    }

    const submitVote = async (optionId: string): Promise<void> => {
        if (isSubmittingRef.current || isPollClosed) {
            return
        }

        isSubmittingRef.current = true
        setIsSubmitting(true)

        try {
            if (!config?.pollingEndpoint) {
                console.warn('Polling endpoint not configured')
                setSubmitErrorMessage(POLL_SUBMIT_ERROR_MESSAGE)
                return
            }

            const sessionId =
                pollSessionIdRef.current ||
                getOrCreatePollStorageValue(pollStorageKey).sessionId

            const url = `${config.pollingEndpoint}/polls/${pollId}/vote`

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    optionId,
                    sessionId,
                }),
            })

            if (response.status === 201 || response.status === 409) {
                if (response.status === 201) {
                    setPollOptions((previousOptions) => {
                        const selectedVoteCount =
                            previousOptions.find(
                                (option) => option.optionId === optionId,
                            )?.voteCount ?? 0
                        const optimisticVoteCount = selectedVoteCount + 1

                        optimisticVoteRef.current = {
                            optionId,
                            minimumVoteCount: optimisticVoteCount,
                        }

                        return previousOptions.map((option) =>
                            option.optionId === optionId
                                ? {
                                      ...option,
                                      voteCount: Math.max(
                                          option.voteCount,
                                          optimisticVoteCount,
                                      ),
                                  }
                                : option,
                        )
                    })
                }

                if (config?.contentApi) {
                    try {
                        await fetchAndApplyPoll()
                    } catch (error) {
                        console.error(
                            'Failed to refresh poll data after vote submission:',
                            error,
                        )
                    }
                }

                setHasVoted(true)
                setSubmitErrorMessage(null)
                const pollStorage = getOrCreatePollStorageValue(pollStorageKey)
                writePollStorageValue(pollStorageKey, {
                    ...pollStorage,
                    hasVoted: true,
                    selectedOption: optionId,
                })
            } else {
                throw new Error(
                    `Failed to submit poll vote: ${response.status} ${response.statusText}`,
                )
            }
        } catch (error) {
            console.error(error)
            setSubmitErrorMessage(POLL_SUBMIT_ERROR_MESSAGE)
        } finally {
            isSubmittingRef.current = false
            setIsSubmitting(false)
        }
    }

    const getPercentage = (votes: number): number => {
        if (totalVotes === 0) return 0

        const percentage = (votes / totalVotes) * 100
        return Math.round(percentage * 100) / 100
    }

    const findAgreementPercentage = (): number => {
        if (!selectedOption) return 0
        const selectedOptionVotes = pollOptions.find(
            (option) => option.optionId === selectedOption,
        )?.voteCount

        if (selectedOptionVotes === undefined) return 0
        return getPercentage(selectedOptionVotes)
    }

    const handleShare = async (): Promise<void> => {
        const shareUrl =
            typeof window !== 'undefined' ? window.location.href : null

        if (!shareUrl) {
            return
        }

        if (
            typeof navigator !== 'undefined' &&
            typeof navigator.share === 'function'
        ) {
            try {
                await navigator.share({
                    title: pollQuestion,
                    text: `I voted in this poll: ${pollQuestion}`,
                    url: shareUrl,
                })
            } catch (e) {
                console.error(e, 'Error while sharing')
            }
        } else if (
            typeof navigator !== 'undefined' &&
            typeof navigator.clipboard?.writeText === 'function'
        ) {
            try {
                await navigator.clipboard.writeText(shareUrl)
            } catch (error) {
                console.error('Error copying to clipboard', error)
            }
        }
    }

    const maxVotedOption = pollOptions.reduce(
        (maxOption: PollOption | null, currentOption: PollOption) => {
            if (!maxOption || currentOption.voteCount > maxOption.voteCount) {
                return currentOption
            }
            return maxOption
        },
        null,
    )

    return (
        <>
            <PollContainer isSevenYouPath={isSevenYouPath}>
                <PollLogo
                    src={pollLogo}
                    alt={isSevenYouPath ? '7You Logo' : '7NEWS Logo'}
                />
                <PollQuestion>{pollQuestion}</PollQuestion>

                {isInitialPollLoading ? (
                    <LoadingContainer aria-label="Loading poll options">
                        <LoadingEllipsis color="grey" />
                    </LoadingContainer>
                ) : !shouldShowResults ? (
                    <>
                        <OptionsContainer>
                            {pollOptions.map((option) => (
                                <OptionLabel
                                    key={option.optionId}
                                    selected={
                                        selectedOption === option.optionId
                                    }
                                >
                                    <OptionInput
                                        type="radio"
                                        name={`poll-${pollId}`}
                                        value={option.optionId}
                                        checked={
                                            selectedOption === option.optionId
                                        }
                                        onChange={() =>
                                            handleOptionChange(option.optionId)
                                        }
                                    />
                                    <OptionText
                                        selected={
                                            selectedOption === option.optionId
                                        }
                                    >
                                        {option.optionText}
                                    </OptionText>
                                </OptionLabel>
                            ))}
                        </OptionsContainer>
                        <SubmitButton
                            type="button"
                            onClick={handleSubmit}
                            disabled={
                                !selectedOption || isSubmitting || isPollClosed
                            }
                        >
                            Submit
                        </SubmitButton>
                        {submitErrorMessage && (
                            <ResultsContainer>
                                <VoteErrorMessage>
                                    {submitErrorMessage}
                                </VoteErrorMessage>
                            </ResultsContainer>
                        )}
                    </>
                ) : (
                    <ResultsContainer>
                        {pollOptions.map((option) => {
                            const percentage = getPercentage(option.voteCount)
                            const isSelected =
                                option.optionId === selectedOption
                            const isMaxVoted =
                                maxVotedOption?.optionId === option.optionId
                            return (
                                <ResultBar
                                    key={option.optionId}
                                    isSelected={isSelected}
                                >
                                    <ResultBarFill
                                        width={percentage}
                                        isSelected={
                                            isSelected ||
                                            (isMaxVoted && !selectedOption)
                                        }
                                    />
                                    <ResultItem isSelected={isSelected}>
                                        <ResultBarContent
                                            isSelected={
                                                isSelected ||
                                                (isMaxVoted && !selectedOption)
                                            }
                                        >
                                            <ResultText>
                                                {option.optionText}
                                            </ResultText>
                                            <ResultPercentage>
                                                {percentage}%
                                            </ResultPercentage>
                                        </ResultBarContent>
                                    </ResultItem>
                                </ResultBar>
                            )
                        })}
                        <ResponseStatus>
                            {hasVoted ? 'Response saved' : 'Poll closed'}
                        </ResponseStatus>
                        <TotalVotes>
                            {hasVoted &&
                            selectedOption &&
                            pollOptions.some(
                                (option) => option.optionId === selectedOption,
                            )
                                ? `${totalVotes} votes. ${findAgreementPercentage()}% agree with you`
                                : `${totalVotes} votes.`}
                        </TotalVotes>
                    </ResultsContainer>
                )}
            </PollContainer>
            {hasVoted && (
                <ShareLinkContainer
                    isSevenYouPath={isSevenYouPath}
                    type="button"
                    aria-label="Share this poll"
                    onClick={handleShare}
                >
                    <span>Share:</span>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                </ShareLinkContainer>
            )}
        </>
    )
}

export const Poll = PollComponent
